import React, { useEffect, useMemo, useRef, useState } from 'react';
import useGameBase from '../../hooks/useGameBase';

function formatDistance(value) {
  const safe = Number.isFinite(value) ? value : 0;
  return Math.round(safe * 100) / 100;
}

function formatCountdown(ms) {
  if (!Number.isFinite(ms)) return '--';
  return Math.max(0, Math.ceil(ms / 1000));
}

function accuracyToColor(accuracy) {
  const safe = Math.max(0, Math.min(100, Number(accuracy) || 0));
  const hue = Math.round(8 + ((safe / 100) * 122));
  return `hsl(${hue} 78% 42%)`;
}

function StatCard({ label, value, accentClass = '' }) {
  return (
    <div className={`rounded-2xl border border-slate-200/70 bg-white/80 p-3 shadow-sm dark:border-slate-700 dark:bg-slate-900/70 ${accentClass}`}>
      <div className="text-[11px] uppercase tracking-[0.12em] text-slate-600 dark:text-slate-300">{label}</div>
      <div className="mt-1 text-lg font-bold text-slate-900 dark:text-slate-100">{value}</div>
    </div>
  );
}

function ColorDropButton({ color, disabled, onDrop }) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onDrop}
      className="h-12 w-12 rounded-md border-2 border-slate-700 bg-white shadow-sm transition hover:scale-105 hover:shadow-md disabled:cursor-not-allowed disabled:opacity-55 dark:border-slate-100 dark:bg-slate-900"
      style={{ backgroundColor: color.hex }}
      title={color.label}
      aria-label={`Add ${color.label} drop`}
    >
      <span className="sr-only">{color.label}</span>
    </button>
  );
}

function LeaderboardTarget({ targetColor }) {
  return (
    <div className="mt-3 rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-800">
      <div className="flex items-center justify-between gap-2">
        <div className="font-semibold">Target</div>
        <div className="text-xs text-slate-600 dark:text-slate-300">Reference</div>
      </div>
      <div className="mt-1 flex items-center gap-2 text-xs text-slate-600 dark:text-slate-300">
        <span
          className="h-12 w-12 rounded border border-slate-800 dark:border-slate-100"
          style={{ backgroundColor: targetColor.hex }}
          aria-hidden
        />
        <span>{targetColor.hex}</span>
        <span>•</span>
        <span>RGB ({targetColor.rgb?.[0]}, {targetColor.rgb?.[1]}, {targetColor.rgb?.[2]})</span>
      </div>
    </div>
  );
}

export default function ColorMixGame({ gameState = {}, gamePlayers = [], user = {}, socket, roomId, onGameAction }) {
  const { submitAction } = useGameBase({ gameState, gamePlayers, user, socket, roomId });

  const myId = user?.socketId || user?.userId || null;
  const targetColor = gameState?.targetColor || { rgb: [0, 0, 0], hex: '#000000' };
  const foundationColors = Array.isArray(gameState?.foundationColors) ? gameState.foundationColors : [];
  const playerMixes = gameState?.playerMixes || {};
  const myMix = myId ? playerMixes[myId] : null;
  const mode = gameState?.mode || 'closest';
  const mixingStyle = gameState?.mixingStyle || 'additive';
  const finished = !!gameState?.finished;
  const rankings = useMemo(
    () => (Array.isArray(gameState?.rankings) ? gameState.rankings : []),
    [gameState?.rankings]
  );
  const winners = Array.isArray(gameState?.winners) ? gameState.winners : [];
  const [busyColor, setBusyColor] = useState(null);
  const [localFeedback, setLocalFeedback] = useState('');
  const countdownRef = useRef(null);
  const [remainingMs, setRemainingMs] = useState(null);

  const dispatchAction = async (action) => {
    if (typeof onGameAction === 'function') {
      return onGameAction(action);
    }
    return submitAction(action);
  };

  useEffect(() => {
    if (!gameState?.roundEndsAt || finished) {
      setRemainingMs(null);
      if (countdownRef.current) {
        clearInterval(countdownRef.current);
        countdownRef.current = null;
      }
      return;
    }

    const update = () => {
      setRemainingMs(Math.max(0, gameState.roundEndsAt - Date.now()));
    };

    update();
    if (countdownRef.current) clearInterval(countdownRef.current);
    countdownRef.current = setInterval(update, 250);

    return () => {
      if (countdownRef.current) {
        clearInterval(countdownRef.current);
        countdownRef.current = null;
      }
    };
  }, [gameState?.roundEndsAt, finished]);

  useEffect(() => {
    if (!localFeedback) return undefined;
    const t = setTimeout(() => setLocalFeedback(''), 1800);
    return () => clearTimeout(t);
  }, [localFeedback]);

  const leaderboardRows = useMemo(() => {
    if (rankings.length > 0) return rankings;
    return (gameState.players || []).map((player) => ({
      socketId: player.socketId,
      username: player.username,
      accuracy: 0,
      distance: 0,
      submitted: false,
    }));
  }, [gameState.players, rankings]);

  const canSubmit = mode === 'submit' && myMix && !myMix.submitted && !finished;
  const controlsDisabled = !myMix || finished || (mode === 'submit' && myMix.submitted);

  const onDrop = async (colorKey) => {
    if (controlsDisabled) return;
    setBusyColor(colorKey);
    try {
      await dispatchAction({ type: 'add-drop', colorKey });
    } finally {
      setBusyColor(null);
    }
  };

  const onResetMix = async () => {
    if (controlsDisabled) return;
    await dispatchAction({ type: 'reset-mix' });
  };

  const onSubmitMix = async () => {
    if (!canSubmit) return;
    await dispatchAction({ type: 'submit-mix' });
    setLocalFeedback('Mix submitted. Waiting for other players.');
  };

  if (!myMix && !finished) {
    return (
      <div className="rounded-3xl border border-slate-200 bg-white p-5 text-slate-800 shadow-sm dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100">
        Waiting for your player state to sync...
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-6xl p-2 text-slate-900 dark:text-slate-100 sm:p-3">
      <div className="overflow-hidden rounded-[30px] border border-cyan-200/70 bg-gradient-to-br from-cyan-50 via-white to-amber-50 shadow-[0_24px_70px_rgba(15,23,42,0.12)] dark:border-cyan-900/60 dark:from-slate-950 dark:via-slate-900 dark:to-cyan-950/80">
        <div className="p-3 sm:p-4">
          {finished && (
            <div className="mb-3 rounded-3xl border border-slate-200 bg-white/90 p-4 dark:border-slate-800 dark:bg-slate-900/80">
              <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">Leaderboard</div>
              <LeaderboardTarget targetColor={targetColor} />
              <div className="mt-3 space-y-2">
                {leaderboardRows.map((row, index) => {
                  const isWinner = winners.includes(row.socketId);
                  const isMe = myId && row.socketId === myId;
                  const playerHex = playerMixes[row.socketId]?.hex || '#ffffff';
                  return (
                    <div
                      key={row.socketId}
                      className={`rounded-2xl border px-3 py-2 text-sm ${isWinner ? 'border-emerald-400 bg-emerald-100/70 text-emerald-900 dark:border-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-100' : 'border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-800'} ${isMe ? 'ring-2 ring-cyan-400/70' : ''}`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div className="font-semibold">#{index + 1} {row.username || row.socketId}</div>
                        <div className="text-xs">{Math.round(row.accuracy || 0)}%</div>
                      </div>
                      <div className="mt-1 flex items-center gap-2 text-xs text-slate-600 dark:text-slate-300">
                        <span
                          className="h-12 w-12 rounded border border-slate-800 dark:border-slate-100"
                          style={{ backgroundColor: playerHex }}
                          aria-hidden
                        />
                        <span>{playerHex}</span>
                        <span>•</span>
                        <span>Distance: {formatDistance(row.distance || 0)}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          {!finished && (
          <>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">
                {mode === 'closest' && 'Closest color when time expires wins.'}
                {mode === 'first90' && 'First player to reach 90% accuracy wins immediately.'}
                {mode === 'submit' && 'Submit your final mix when ready.'}
                {' '}
                {mixingStyle === 'subtractive' ? 'Paint-style subtractive mixing is enabled.' : 'Light-style additive mixing is enabled.'}
              </p>
            </div>
            <div className="flex gap-2">
              <StatCard label="Difficulty" value={String(gameState?.difficulty || 'medium').toUpperCase()} />
              <StatCard label="Mixing" value={mixingStyle === 'subtractive' ? 'PAINT' : 'RGB'} />
              <StatCard label="Time Left" value={`${formatCountdown(remainingMs)}s`} />
            </div>
          </div>

          {/* {finished && (
            <div className="mt-4 rounded-2xl border border-emerald-300 bg-emerald-100/80 px-4 py-3 text-sm font-semibold text-emerald-900 dark:border-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-100">
              {winners.length > 1 ? 'Tie game.' : 'Winner decided.'} {gameState?.endedReason ? `(${gameState.endedReason})` : ''}
            </div>
          )} */}

          <div className="mt-3 grid gap-4 grid-cols-1">
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-2xl border border-slate-200 bg-white/90 p-3 dark:border-slate-800 dark:bg-slate-900/80">
                  <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">Target</div>
                  <div className="mt-2 flex items-center gap-2">
                    <div
                      className="h-12 w-12 rounded-xl border border-black/15 shadow-inner"
                      style={{ backgroundColor: targetColor.hex }}
                      aria-label={`Target color ${targetColor.hex}`}
                    />
                    <div>
                      <div className="text-base font-bold text-slate-900 dark:text-slate-100">{targetColor.hex}</div>
                      <div className="text-[11px] text-slate-600 dark:text-slate-300">
                        RGB ({targetColor.rgb?.[0]}, {targetColor.rgb?.[1]}, {targetColor.rgb?.[2]})
                      </div>
                    </div>
                  </div>
                </div>

                <div className="rounded-2xl border border-slate-200 bg-white/90 p-3 dark:border-slate-800 dark:bg-slate-900/80">
                  <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">Your Mix</div>
                  <div className="mt-2 grid grid-cols-[auto_1fr] gap-3">
                    <div
                      className="h-12 w-12 rounded-xl border border-black/20 shadow-inner"
                      style={{ backgroundColor: myMix?.hex || '#ffffff' }}
                      aria-label={`Your color ${myMix?.hex || '#ffffff'}`}
                    />
                    <div className="space-y-1.5 text-xs text-slate-700 dark:text-slate-200">
                      <div className="font-semibold">{myMix?.hex || '#ffffff'}</div>
                      <div className="font-semibold" style={{ color: accuracyToColor(myMix?.accuracy) }}>
                        {Math.round(myMix?.accuracy || 0)}%
                      </div>
                      <div>Distance: {formatDistance(myMix?.distance || 0)}</div>
                    </div>
                  </div>
                </div>
              </div>

              {!finished && (
              <div className="rounded-2xl border border-slate-200 bg-white/90 p-3 dark:border-slate-800 dark:bg-slate-900/80">
                <div className="flex flex-wrap items-center gap-6">
                  {foundationColors.map((color) => (
                    <ColorDropButton
                      key={color.key}
                      color={color}
                      disabled={controlsDisabled || busyColor === color.key}
                      onDrop={() => onDrop(color.key)}
                    />
                  ))}
                </div>

                <div className="mt-3 flex flex-wrap gap-2">
                  <button
                    type="button"
                    disabled={controlsDisabled}
                    onClick={onResetMix}
                    className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-60 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
                  >
                    Reset
                  </button>
                  {mode === 'submit' && (
                    <button
                      type="button"
                      disabled={!canSubmit}
                      onClick={onSubmitMix}
                      className="rounded-lg border border-emerald-600 bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-emerald-500 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {myMix?.submitted ? 'Submitted' : 'Submit'}
                    </button>
                  )}
                </div>

                {localFeedback && (
                  <div className="mt-2 rounded-lg bg-cyan-100/80 px-2.5 py-1.5 text-xs text-cyan-900 dark:bg-cyan-900/30 dark:text-cyan-100">
                    {localFeedback}
                  </div>
                )}
              </div>
              )}
            </div>

            {!finished && (
            <div className="rounded-3xl border border-slate-200 bg-white/90 p-4 dark:border-slate-800 dark:bg-slate-900/80">
              <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">Leaderboard</div>
              <LeaderboardTarget targetColor={targetColor} />
              <div className="mt-3 space-y-2">
                {leaderboardRows.map((row, index) => {
                  const isWinner = winners.includes(row.socketId);
                  const isMe = myId && row.socketId === myId;
                  return (
                    <div
                      key={row.socketId}
                      className={`rounded-2xl border px-3 py-2 text-sm ${isWinner ? 'border-emerald-400 bg-emerald-100/70 text-emerald-900 dark:border-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-100' : 'border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-800'} ${isMe ? 'ring-2 ring-cyan-400/70' : ''}`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div className="font-semibold">#{index + 1} {row.username || row.socketId}</div>
                        <div className="text-xs">{Math.round(row.accuracy || 0)}%</div>
                      </div>
                      <div className="mt-1 text-xs text-slate-600 dark:text-slate-300">
                        Distance: {formatDistance(row.distance || 0)}
                        {mode === 'submit' ? ` • ${row.submitted ? 'Submitted' : 'Editing'}` : ''}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            )}
          </div>
          </>
          )}
        </div>
      </div>
    </div>
  );
}

ColorMixGame.Guide = function ColorMixGuide() {
  return (
    <div>
      <h3 className="font-bold">Color Mix</h3>
      <p>Tap foundation colors to add drops and move your paint toward the target.</p>
      <p>Config includes mixing style: additive RGB (light) or subtractive pigment (paint-like).</p>
      <p>Your score is based on Euclidean RGB distance, converted to an accuracy percentage out of 100.</p>
      <p>Modes: closest on timeout, first to 90%, or submit when ready.</p>
    </div>
  );
};
